import { AppBar, Tab } from "@mui/material";
import { withStyles, WithStyles } from "@mui/styles";
import { FeedStyleClasses, TimelineStyleClasses } from "assets/jss/common-styles";
import AdvFilter, { AdvFilterCommand, AdvFilterComponent, AdvFilterPicker, AdvFilterResult } from "components/AdvFilter";
import { CmdList, CmdPoll, CmdPost, CmdPublishEndDate, CmdPublishStartDate, CmdQuestion, CmdWithFile, CmdWithImage, CmdWithVideo, CommandLabels } from "components/AdvFilter/CommonCommands";
import { AppBarTabs } from "components/AppTabs";
import ButtonResetFilter from "components/Buttons/ButtonResetFilter";
import { RequestErrorReport } from "components/CallServer/RequestErrorReport";
import CountFilter from "components/CountFilter";
import { MuiFilterAppBar, MuiFilterLine, MuiFilterToolbar } from "components/MuiExtras/Filter";
import MuiMessageBox from "components/MuiExtras/MessageBox/MuiMessageBox";
import MuiContainer from "components/MuiExtras/MuiContainer";
import { withSnackbar, WithSnackbar } from "components/SnackbarProvider/Snackbar";
import LibUtils from "lib/LibUtils";
import { FeedType, PublicationType } from "models/ApiEnums";
import { FeedHistoryFilter } from "models/ApiModels";
import * as React from "react";
import { useTranslation, WithTranslation, withTranslation } from "react-i18next";
import { connect } from "react-redux";
import { Action } from "redux";
import { ThunkDispatch } from "redux-thunk";
import * as FeedActions from 'store/feed/actions';
import { FeedState } from "store/feed/reducer";
import { ApplicationState } from "store/rootReducer";
import * as SideBarActions from 'store/sidebar/actions';
import CardManager from "views/Timeline/Components/CardManager";
import { ITimelineDispatchProps } from "views/Timeline/TimelineCommons";

/////////////////////////////////////////////////////////////////////////////////////////////////

interface IStateProps {
    feed: FeedState;
}

interface IState {
    advFilters: AdvFilterResult[];
    filter: FeedHistoryFilter;
    dialog: any;
}

interface IStateSaved {
    feedType: FeedType;
    state: IState;
}

interface IProps {
    timestamp?: Date;
}

interface IDispathProps extends ITimelineDispatchProps {
    onGetFeed: (filter: FeedHistoryFilter, startIndex: number, endIndex: number) => Promise<any>;
}

type Props = IProps & IDispathProps & IStateProps & WithSnackbar & WithTranslation & WithStyles<typeof TimelineStyleClasses> & WithStyles<typeof FeedStyleClasses>;

/////////////////////////////////////////////////////////////////////////////////////////////////

interface IRenderMenu {
    category: FeedType;
    onChange: (event: React.ChangeEvent<{}>, value: any) => void;
}

const RenderMenuBar: React.FC<Props & IRenderMenu> = (props) => {
    const { t } = useTranslation();

    return (
        <div className={props.classes.filterTabContainer}>
            <AppBar position="static" color="default" classes={{ root: props.classes.appTabFilterRoot }}>
                <AppBarTabs
                    centered
                    value={props.category}
                    onChange={props.onChange}
                >
                    <Tab value={FeedType.HistoryRecent} label={t("Views.Timeline.TimelineHistory.TabRecent")} disabled={props.feed.loading} />
                    <Tab value={FeedType.HistoryLiked} label={t("Views.Timeline.TimelineHistory.TabLiked")} disabled={props.feed.loading} />
                    <Tab value={FeedType.HistoryCommented} label={t("Views.Timeline.TimelineHistory.TabCommented")} disabled={props.feed.loading} />
                    <Tab value={FeedType.HistoryThanks} label={t("Views.Timeline.TimelineHistory.TabThanked")} disabled={props.feed.loading} />
                    {/*
                     * Mesmo no desktop, o layout responsivo corta as opções quando diminui o tamanho da tela
                     * Melhor deixar sem esta opção por enquanto.
                    {
                        !HtmlUtils.isIPad() &&
                        <Tab value={FeedType.HistoryShared} label={t("Views.Timeline.TimelineHistory.TabShared")} />
                    }
                    */}
                </AppBarTabs>
            </AppBar>
        </div>
    );
};

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////


class TimelineHistoryComponent extends React.PureComponent<Props, IState> {

    private advFilterRef: AdvFilterComponent;

    private saveState: IStateSaved[] = [];

    constructor(props: Props) {
        super(props);

        this.saveState.push({ feedType: FeedType.HistoryRecent, state: this.recoverState(FeedType.HistoryRecent) });
        this.saveState.push({ feedType: FeedType.HistoryLiked, state: this.recoverState(FeedType.HistoryLiked) });
        this.saveState.push({ feedType: FeedType.HistoryCommented, state: this.recoverState(FeedType.HistoryCommented) });
        this.saveState.push({ feedType: FeedType.HistoryThanks, state: this.recoverState(FeedType.HistoryThanks) });
        this.saveState.push({ feedType: FeedType.HistoryShared, state: this.recoverState(FeedType.HistoryShared) });

        this.state = this.saveState[0].state;
    }

    ///
    private saveCurrentState() {
        var item = this.saveState.find(p => p.feedType == this.state.filter.category);
        item.state = this.state;
    }

    ///
    private recoverState(feedType: FeedType): IState {

        var item = this.saveState.find(p => p.feedType == feedType);

        if (item == null)
            return {
                advFilters: [],
                filter: {
                    category: feedType,
                    publicationTypes: null,
                    searchText: null,
                    startDate: null,
                    endDate: null,
                    hasFile: null,
                    hasImage: null,
                    hasVideo: null
                },
                dialog: null
            } as IState;

        return item.state;
    }


    //#region FILTER

    ///
    private get commands(): AdvFilterCommand[] {
        const cmdLabels = CommandLabels();
        return [
            { keyword: PublicationType[PublicationType.Post], displayName: cmdLabels[CmdPost], group: 1 },
            { keyword: PublicationType[PublicationType.Question], displayName: cmdLabels[CmdQuestion], group: 1 },
            { keyword: PublicationType[PublicationType.List], displayName: cmdLabels[CmdList], group: 1 },
            { keyword: PublicationType[PublicationType.Poll], displayName: cmdLabels[CmdPoll], group: 1 },

            { keyword: CmdWithVideo, displayName: cmdLabels[CmdWithVideo], group: 2 },
            { keyword: CmdWithImage, displayName: cmdLabels[CmdWithImage], group: 2 },
            { keyword: CmdWithFile, displayName: cmdLabels[CmdWithFile], group: 2 },

            { keyword: CmdPublishStartDate, displayName: cmdLabels[CmdPublishStartDate], picker: AdvFilterPicker.Calendar, group: 3 },
        ];
    }

    ///
    private handleTabSelection = (event: React.ChangeEvent<{}>, value: any) => {
        if (this.props.feed.loading && this.state.filter.category == value) {
            return;
        }

        this.saveCurrentState();
        this.setState(this.recoverState(value));
        this.props.onFeedReset();
    };

    ///
    private clearFilter(): Partial<FeedHistoryFilter> {
        return {
            publicationTypes: null,
            searchText: null,
            startDate: null,
            endDate: null,
            hasFile: null,
            hasImage: null,
            hasVideo: null
        };
    }

    ///
    private buildFilter(searchText: string, advFilters: AdvFilterResult[]) {
        var filter: Partial<FeedHistoryFilter> = {};
        filter.searchText = searchText.length == 0 ? null : searchText;

        filter.hasVideo = advFilters.findIndex(x => x.keyword == CmdWithVideo) > -1;
        filter.hasImage = advFilters.findIndex(x => x.keyword == CmdWithImage) > -1;
        filter.hasFile = advFilters.findIndex(x => x.keyword == CmdWithFile) > -1;

        filter.publicationTypes = [];
        if (advFilters.findIndex(x => x.keyword == PublicationType[PublicationType.List]) > -1) filter.publicationTypes.push(PublicationType.List);
        if (advFilters.findIndex(x => x.keyword == PublicationType[PublicationType.Post]) > -1) filter.publicationTypes.push(PublicationType.Post);
        if (advFilters.findIndex(x => x.keyword == PublicationType[PublicationType.Question]) > -1) filter.publicationTypes.push(PublicationType.Question);
        if (advFilters.findIndex(x => x.keyword == PublicationType[PublicationType.Poll]) > -1) filter.publicationTypes.push(PublicationType.Poll);
        if (advFilters.findIndex(x => x.keyword == PublicationType[PublicationType.Survey]) > -1) filter.publicationTypes.push(PublicationType.Survey);
        if (advFilters.findIndex(x => x.keyword == PublicationType[PublicationType.Course]) > -1) filter.publicationTypes.push(PublicationType.Course);

        var dt = advFilters.find(x => x.keyword == CmdPublishStartDate);
        filter.startDate = dt ? (dt.value as Date).putStartOfDay() : null;

        dt = advFilters.find(x => x.keyword == CmdPublishEndDate);
        filter.endDate = dt ? (dt.value as Date).putEndOfDay() : null;

        return filter;
    }

    ///
    private isFiltered() {
        return !LibUtils.isAllPropertiesWithValue(this.state.filter, null, ["category"]);
    }

    ///
    private handleUpdateFilter = (searchText: string, advFilters: AdvFilterResult[]) => {
        var newFilter = (searchText.length == 0 && advFilters.length == 0)
            ? this.clearFilter()
            : this.buildFilter(searchText, advFilters);

        this.setState(
            {
                advFilters,
                filter: { ...this.state.filter, ...newFilter }
            }, () => this.props.onFeedReset()
        );
    };

    ///
    private renderAdvFilter(): JSX.Element {
        return (
            <MuiFilterAppBar position="static">
                <MuiFilterToolbar variant="dense" >
                    <MuiFilterLine>
                        <AdvFilter
                            id="advfilter"
                            commands={this.commands}
                            innerRef={(ref) => this.advFilterRef = ref}
                            placeHolder={this.props.t("Common.Filter.Adv.InputPlaceholder")}
                            placeHolderFocus={this.props.t("Common.Filter.Adv.InputPlaceholderFocus")}
                            title={this.props.t("Common.Filter.Adv.InputTitleEnterToFilter")}
                            selectedCommands={this.state.advFilters}
                            underlineColor="white"
                            onUpdateSelection={this.handleUpdateFilter}
                        />
                    </MuiFilterLine>
                </MuiFilterToolbar>
            </MuiFilterAppBar>
        );
    }

    //#endregion

    ///
    public componentDidMount() {
        this.props.onFeedReset();
    }

    ///
    public componentWillUnmount() {
        this.props.onFeedReset();
    }

    ///
    public componentDidUpdate(prevProps: Props) {
        if (prevProps.timestamp != this.props.timestamp && !this.props.feed.loading)
            this.props.onFeedReset();
    }

    ///
    public render() {
        const { classes, feed, feed: { lastError, totalRecords, loading }, snackbar } = this.props;
        const { filter: { category } } = this.state;

        var notFoundMessage = "";
        switch (category) {
            case FeedType.HistoryRecent: notFoundMessage = this.props.t("Views.Timeline.TimelineHistory.TabRecentEmpty"); break;
            case FeedType.HistoryLiked: notFoundMessage = this.props.t("Views.Timeline.TimelineHistory.TabLikedEmpty"); break;
            case FeedType.HistoryCommented: notFoundMessage = this.props.t("Views.Timeline.TimelineHistory.TabCommentedEmpty"); break;
            case FeedType.HistoryThanks: notFoundMessage = this.props.t("Views.Timeline.TimelineHistory.TabThankedEmpty"); break;
            case FeedType.HistoryShared: notFoundMessage = this.props.t("Views.Timeline.TimelineHistory.TabSharedEmpty"); break;
        }

        return (
            <div>

                <RenderMenuBar {...this.props} category={category} onChange={this.handleTabSelection} />

                <div className={classes.timelineContainer}>

                    {this.state.dialog}

                    {
                        (totalRecords > 0 || this.isFiltered()) &&
                        <MuiContainer margin="normal">
                            {this.renderAdvFilter()}
                        </MuiContainer>
                    }

                    {
                        totalRecords > 0 &&
                        <MuiContainer margin="normal">
                            <CountFilter
                                count={totalRecords}
                                filtered={this.isFiltered()}
                                resource="Common.Feed.Filter.PublicationResultText"
                                filteredResource="Common.Feed.Filter.PublicationFilteredResultText"
                                onResetFilter={() => this.advFilterRef.resetFilter()}
                            />
                        </MuiContainer>
                    }

                    <CardManager
                        classes={classes}
                        feedType={category}
                        feed={feed}
                        snackbar={snackbar}
                        onExpandPublication={this.props.onExpandPublication}
                        onReloadPublication={this.props.onReloadPublication}
                        onRemovePublication={this.props.onRemovePublication}
                        onUpdatePublication={this.props.onUpdateFeedData}
                        onSidebarResetRequest={this.props.onSidebarResetRequest}
                        onRemoveFromFeed={this.props.onRemoveFromFeed}
                        onGetFeed={(startIndex: number, endIndex: number) => { return this.props.onGetFeed(this.state.filter, startIndex, endIndex); }}
                        onFeedReset={() => this.props.onFeedReset()}
                    />

                    {
                        lastError != null
                            ? <RequestErrorReport error={lastError} />
                            : totalRecords == 0 && !loading && !this.isFiltered()
                                ? (
                                    <MuiMessageBox
                                        variant="info"
                                        message={notFoundMessage}
                                    />
                                )
                                : totalRecords == 0 && !loading && this.isFiltered()
                                    ? (
                                        <MuiMessageBox
                                            variant="warning"
                                            action={<ButtonResetFilter color="warning" onClick={() => this.advFilterRef.resetFilter()} />}
                                            message={this.props.t("Common.Feed.Filter.PublicationFilteredResultText_zero")}
                                        />
                                    )
                                    : null
                    }
                </div>
            </div>

        );
    }
}

/////////////////////////////////////////////////////////////////////////////////////////////////

function mapStateToProps(state: ApplicationState): IStateProps {
    return {
        feed: state.feed
    };
}

function mapDispatchToProps(dispatch: ThunkDispatch<ApplicationState, void, Action<any>>, ownProps: IProps): IDispathProps {
    const action = new FeedActions.HistoryFeedAction();

    return {
        onAddPublication: (publicationId) => dispatch(action.addPublication(publicationId)),
        onReloadPublication: (uid, publicationId) => dispatch(action.reloadPublication(uid, publicationId)),
        onRemovePublication: (uid, publicationId, publicationType) => dispatch(action.removePublication(uid, publicationId, publicationType)),

        onExpandPublication: (uid, publicationId) => dispatch(action.getPublicationFullContent(uid, publicationId)),
        onUpdateFeedData: (newItemDto) => dispatch(action.updateFeedData(newItemDto)),
        onRemoveFromFeed: (uid, publicationId) => dispatch(action.removeFeedData(uid, publicationId)),
        onFeedReset: () => dispatch(action.feedReset()),

        onGetFeed: (filter, startIndex, endIndex) => dispatch(action.getFeed(filter, startIndex, endIndex)),

        onSidebarResetRequest: () => dispatch(SideBarActions.reset()),

        executeFeedMessage: (messageType) => action.executeFeedMessage(messageType, dispatch)
    };
}

const withAllStyles = withStyles(
    (theme) => ({
        ...TimelineStyleClasses(theme),
        ...FeedStyleClasses(theme),
    }),
    { withTheme: true },
);

const Timeline = connect<IStateProps, IDispathProps>(mapStateToProps, mapDispatchToProps)(withSnackbar(withTranslation()(withAllStyles(TimelineHistoryComponent))));

///////////////////////////////////////////////////////////////////////////

const TimelineHistory: React.FC<Props> = () => {
    return (
        <Timeline
            timestamp={new Date()}
        />
    );
};

export default TimelineHistory;